// Board route (/p/:key/boards/:streamKey): the stream's board full-width, the
// task drawer on `?taskId=`, and the "New task" / "Report bug" drawer.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import type { Project } from '@goblic/shared'
import { streamsApi } from '../../lib/api-client'
import { RequireProject } from '../../lib/project-context'
import { BoardPage } from '../../components/board/board-page'
import { TaskCreateDrawer, TaskDrawer } from '../../components/tasks/task-drawer'

/** `/p/$projectKey/boards/$streamKey` search params (router.tsx). */
export interface BoardSearch {
  taskId?: string
}

export function ProjectBoardPage() {
  return <RequireProject>{(project) => <BoardContent project={project} />}</RequireProject>
}

function BoardContent({ project }: { project: Project }) {
  const { streamKey } = useParams({ from: '/_authenticated/p/$projectKey/boards/$streamKey' })
  const search = useSearch({ from: '/_authenticated/p/$projectKey/boards/$streamKey' })
  const navigate = useNavigate()
  const [creatingIn, setCreatingIn] = useState<string | null>(null)

  // Same query (and key) as the board header's stream tabs.
  const streamsQuery = useQuery({
    queryKey: ['project', project.id, 'streams'],
    queryFn: () => streamsApi.list(project.id),
  })
  const creatingStream = streamsQuery.data?.items.find((s) => s.key === creatingIn)

  function openTask(taskId: string | undefined): void {
    void navigate({
      to: '/p/$projectKey/boards/$streamKey',
      params: { projectKey: project.key, streamKey },
      search: taskId ? { taskId } : {},
      replace: taskId === undefined,
    })
  }

  return (
    <div className="flex h-full flex-col px-4 pt-6 sm:px-6">
      <BoardPage
        project={project}
        streamKey={streamKey}
        onSelectStream={(next) =>
          void navigate({
            to: '/p/$projectKey/boards/$streamKey',
            params: { projectKey: project.key, streamKey: next },
          })
        }
        onOpenTask={openTask}
        onNewTask={setCreatingIn}
      />
      {creatingIn ? (
        <TaskCreateDrawer
          project={project}
          workflowKey={creatingStream?.workflowKey ?? null}
          onClose={() => setCreatingIn(null)}
          onCreated={(task) => {
            setCreatingIn(null)
            openTask(task.id)
          }}
        />
      ) : null}
      {search.taskId ? (
        <TaskDrawer taskId={search.taskId} initialTab="details" onClose={() => openTask(undefined)} />
      ) : null}
    </div>
  )
}
